import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { addToast } from 'components/Toast/Toast'
import { AllIcons } from 'helpers/allIcons'
import { MainSettingsPage } from 'pages/settings/layout/MainSettingsPage'
import {
    putChatSettings,
    putRemoveDuplicates
} from 'services/endpoints/settings/general/chat'
import { useAuthStore, useUserProfile } from 'store'

export const ChatSettings = () => {
    const [chatSettingsState, setChatSettingsState] = useState<
        Record<string, boolean>
    >({
        welcome_message_enabled: false,
        connect_instagram_enabled: false,
        remove_duplicates: false
    })

    const userData = useUserProfile()
    const { t } = useTranslation()

    useEffect(() => {
        if (userData) {
            setChatSettingsState({
                welcome_message_enabled:
                    !!userData.chat_settings?.welcome_message_enabled,
                connect_instagram_enabled:
                    !!userData.chat_settings?.connect_instagram_enabled,
                remove_duplicates: !!userData.remove_duplicates
            })
        }
    }, [userData])

    const toggleChatSettingMutation = useMutation(
        async (newChatSettings: { key: string; value: boolean }) => {
            const { key, value } = newChatSettings

            setChatSettingsState(prev => ({
                ...prev,
                [key]: value
            }))

            return await putChatSettings({ [key]: value })
        },
        {
            onError: err => {
                console.error(err)
                addToast('error', t('error:errorSomethingWentWrong'))
            },
            onSuccess: (_data, variables) => {
                const { key, value } = variables

                const currentProfile = useAuthStore.getState().userProfile
                if (!currentProfile) return

                useAuthStore.getState().setUserProfile({
                    chat_settings: {
                        ...currentProfile.chat_settings,
                        [key]: value
                    }
                })
            }
        }
    )

    const toggleRemoveDuplicatesMutation = useMutation(
        async (value: boolean) => {
            setChatSettingsState(prev => ({
                ...prev,
                remove_duplicates: value
            }))

            return await putRemoveDuplicates({ remove_duplicates: value })
        },
        {
            onError: err => {
                console.error(err)
                addToast('error', t('error:errorSomethingWentWrong'))
            },
            onSuccess: (_data, value) => {
                useAuthStore.getState().setUserProfile({
                    remove_duplicates: value
                })
            }
        }
    )

    const chatSettingsCards = [
        {
            body: t('duplicateMedia'),
            info: t('removeDuplicateRecipients'),
            hasToggle: true,
            toggleActive: chatSettingsState.remove_duplicates,
            toggleFn: () => {
                toggleRemoveDuplicatesMutation.mutate(
                    !chatSettingsState.remove_duplicates
                )
            },
            link: '/settings/general?chat-settings&remove-duplicates',
            hasArrow: true
        },
        {
            body: t('chatStrategy'),
            info: t('chatStrategyDetail'),
            hasToggle: true,
            toggleActive: chatSettingsState.remove_duplicates,
            toggleFn: () => {
                toggleRemoveDuplicatesMutation.mutate(
                    !chatSettingsState.remove_duplicates
                )
            },
            link: '/settings/general?chat-settings&chat-strategy',
            hasArrow: true
        },
        {
            body: t('welcomeMessage'),
            info: t('setUpAnAutoMessageToYourNewSubscribers'),
            hasToggle: true,
            toggleActive: chatSettingsState.welcome_message_enabled,
            toggleFn: () => {
                toggleChatSettingMutation.mutate({
                    key: 'welcome_message_enabled',
                    value: !chatSettingsState.welcome_message_enabled
                })
            },
            link: '/settings/general?chat-settings&welcome-message',
            hasArrow: true
        },
        {
            icon: AllIcons.instagram_active,
            body: t('instagram'),
            hasToggleWithText: true,
            toggleText: chatSettingsState.connect_instagram_enabled
                ? t('connected')
                : t('connect'),
            toggleActive: chatSettingsState.connect_instagram_enabled,
            toggleFn: () => {
                toggleChatSettingMutation.mutate({
                    key: 'connect_instagram_enabled',
                    value: !chatSettingsState.connect_instagram_enabled
                })
            },
            link: '/settings/general?chat-settings&connect-instagram',
            hasArrow: false
        }
    ]

    return <MainSettingsPage settingsTop={chatSettingsCards} />
}